import { useAuth0 } from '@auth0/auth0-react'
import { t } from '@lingui/macro'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { DataProvider } from '@/providers/DataProvider'
import { UrlParamsProvider } from '@/providers/UrlParamsProvider/UrlParamsProvider'
import { ExchangeProvider } from '@/providers/ExchangeProvider'
import { SetupProvider } from '@/providers/SetupProvider'
import { overviewActions } from '@/redux/overview/overview.slice'
import { profileActions } from '@/redux/profile/profile.slice'
import { PUBLIC_ROUTES } from '@/router/urls'
import { setupAxiosMainService } from '@/services/axios'
import { ErrorBase } from '@/ui/kit/Errors/ErrorBase'
import { Loader } from '@/ui/molecules/Loader'
import { GoogleAuthenticatorForm } from '@/ui/organisms/GoogleAuthenticatorForm'
import { useActions, useAppSelector } from '@/utils'
import { useLogout } from '@/utils/hooks/useLogout'
import { storage } from '@/utils/lib/storage'
import { useLingui } from '@lingui/react'
import { TExchangesAvailable, TLanguage } from '@/core/constants'
import { useExchageType } from '@/utils/hooks/useExchangeType'
import { INCOME_DEFAULT } from '@/redux/overview/overview.defaults'
import { setupOKXAxiosService } from '@/services/axios/utils/setAuthOKXAxiosService'
import { E_AmplitudeEventName } from '@/redux/profile/profile.types'
import { ButtonVariant } from '@/ui/kit'
import { defineMetadata } from '@/utils/lib/metadata/mapper'
import { useTrackEvent } from '@/utils/hooks/useTrackEvent'

export interface IAuthHandlerProviderProps {
  children: React.ReactNode
}

export const AuthHandlerProvider: React.FC<IAuthHandlerProviderProps> = props => {
  const [authError, setAuthError] = useState<string | null>(null)
  const { pathname } = useLocation()
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently, error, user } = useAuth0()
  const { GetProfileTC, setEmail, setTigerTradeId, UpdateMetadata } = useActions(profileActions)
  const { setIncomeStatistics, resetIncomeStatistics } = useActions(overviewActions)
  const {
    initialStatus: profileStatus,
    metadata,
    loginTwoFa,
    verifiedWithTwoFaStatus,
    errors,
    _metaProfileLocale,
  } = useAppSelector(state => state.profile)
  const logoutWrapper = useLogout()
  const [isSetup, setSetup] = useState(false)
  const track = useTrackEvent()
  const { i18n } = useLingui()

  const { exchangeType } = useExchageType()

  const isPublic = useMemo(() => PUBLIC_ROUTES.some(route => pathname.startsWith(route)), [pathname])

  const { setErrors, VerifyTwoFA, UpdateLocale } = useActions(profileActions)
  const { statisticsStartMoment, isFirstSessionTracked, isNeedEmptyStatistic } = metadata

  const handleVerifyTwoFa = async (value: string) => {
    VerifyTwoFA({ sms: '', google: value })
  }

  const logoutUser = () => {
    logoutWrapper({ logoutParams: { returnTo: window.location.origin } })
  }

  // check error from uri
  const { search } = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])
  const errorFromUrl = searchParams.get('error')
  const errorDescription = searchParams.get('error_description')
  //

  useEffect(() => {
    if (isLoading) return
    if (isAuthenticated) return
    if (!isPublic) return
    setSetup(true)
  }, [isLoading, isAuthenticated, isPublic])

  useEffect(() => {
    if (isLoading) return
    if (isPublic) return
    if (isAuthenticated) return
    if (errorFromUrl) return
    loginWithRedirect() // @see https://tigertrade.atlassian.net/browse/TTS-1581
  }, [isLoading, isPublic, isAuthenticated, loginWithRedirect])

  useEffect(() => {
    if (isSetup) return
    if (isLoading) return
    if (!isAuthenticated) return
    loadToken()
  }, [isAuthenticated, isSetup, isLoading])

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email)
    }
  }, [user])

  const MAX_RETRIES = 5 // Лимит попыток получения токена
  let retryCount = 0 // Глобальный счётчик попыток

  const loadToken = async () => {
    getAccessTokenSilently()
      .then(token => {
        try {
          const tokenData = JSON.parse(atob(token?.split('.')?.[1]))
          // TODO: move namespace to .env
          setTigerTradeId(tokenData?.['https://tiger.trade/internalId'])
        } catch (e) {}

        setupAxiosMainService({ accessToken: token })
        setupOKXAxiosService({ accessToken: token })
      })

      .catch(e => {
        console.error('Auth0 token error:', e)

        const LOGOUT_DELAY = 2000 // 2 сек задержки

        const delayedLogout = () => {
          console.warn(`Logging out in ${LOGOUT_DELAY / 1000} seconds...`)
          setTimeout(() => {
            logoutWrapper()
          }, LOGOUT_DELAY)
        }

        switch (e.error) {
          case 'login_required':
          case 'consent_required':
          case 'interaction_required':
            console.warn('Redirecting to login. Auth0 Error =', e.error)
            setTimeout(() => {
              loginWithRedirect()
            }, 1500)
            return

          case 'invalid_grant':
          case 'invalid_token':
            console.error('Invalid token, logging out. Auth0 Error =', e.error)
            return delayedLogout()

          case 'access_denied':
            console.warn('Access denied, logging out. Auth0 Error =', e.error)

            return delayedLogout()

          case 'timeout':
          case 'network_error':
          case 'server_error':
            if (retryCount < MAX_RETRIES) {
              retryCount++
              console.warn(`Retrying auth (${retryCount}/${MAX_RETRIES})...`)
              return setTimeout(loadToken, 2000) // Повтор через 2 сек
            }
            // Когда попытки исчерпаны, показываем ошибку через ErrorBase
            setAuthError(
              `Authentication error: ${e.error || 'Unknown error'}.\n` +
                `All connection attempts have been exhausted. You will be logged out automatically in 3 seconds.`
            )

            setTimeout(() => {
              setAuthError(null) // Убираем сообщение через 3 секунды
              logoutWrapper()
            }, 3000)

            return

          default:
            console.error('Unknown Auth0 error when load token, logout after 2 seconds', e)
            return delayedLogout()
        }
      })
      .then(() => GetProfileTC())
      .then(() => setSetup(true))
  }

  /**
   * We check whether the locale has arrived
   * if not, then add it manually
   */
  useEffect(() => {
    if (profileStatus === 'succeeded' && !_metaProfileLocale) {
      UpdateLocale({ locale: i18n.locale as TLanguage })
    }
  }, [profileStatus, _metaProfileLocale])

  useEffect(() => {
    if (profileStatus === 'succeeded' && statisticsStartMoment) {
      setIncomeStatistics([
        exchangeType as TExchangesAvailable,
        'BINANCE_FUTURE',
        { ...INCOME_DEFAULT, startMoment: statisticsStartMoment },
      ])
    }
  }, [profileStatus, statisticsStartMoment])

  const resetStatistics = useCallback(() => {
    const resetMoment = new Date().getTime().toString()
    resetIncomeStatistics([exchangeType as TExchangesAvailable, resetMoment])
    UpdateMetadata(defineMetadata({ isNeedEmptyStatistic: true, statisticsStartMoment: '' }))
  }, [UpdateMetadata])
  useEffect(() => {
    if (!statisticsStartMoment && !isNeedEmptyStatistic) return
    const checkResetTime = () => {
      const now = new Date()
      if (now.getUTCHours() === 0 && now.getUTCMinutes() === 0 && now.getUTCSeconds() === 1) {
        resetStatistics()
      }
    }
    const now = new Date()
    if (now.getUTCHours() === 0 && now.getUTCMinutes() === 0 && now.getUTCSeconds() > 1) {
      resetStatistics()
    }

    const intervalId = setInterval(checkResetTime, 1000)

    return () => clearInterval(intervalId)
  }, [resetStatistics, UpdateMetadata, statisticsStartMoment])

  useEffect(() => {
    if (profileStatus === 'succeeded' && !isFirstSessionTracked) {
      UpdateMetadata({
        isFirstSessionTracked: 'true',
        firstSesstionTrackedTimestamp: new Date().getTime().toString(),
      })
    }
  }, [profileStatus, isFirstSessionTracked])

  useEffect(() => {
    if (!isFirstSessionTracked) {
      track(E_AmplitudeEventName.VisitAccountTigerCom)
    }
  }, [isFirstSessionTracked])

  const errorBaseProps = useMemo(() => {
    return {
      customHeader: t({ message: `Couldn’t load data`, id: 'core.notLoadData' }),
      isRefreshButtonVisible: true,
      isSupportMessage: true,
    }
  }, [])

  if (authError) {
    return (
      <Loader.Overlay>
        <ErrorBase customHeader={t({ message: `Authentication error`, id: 'core.authError' })} customText={authError} />
      </Loader.Overlay>
    )
  }

  if (errorFromUrl) {
    return (
      <Loader.Overlay>
        <ErrorBase
          customHeader={t({ message: `Authentication error`, id: 'core.authError' })}
          customText={`${errorFromUrl}: ${errorDescription}`}
          // isRefreshButtonVisible
          isSupportMessage
          onClickCustomButton={logoutUser}
          customButtonVariant={ButtonVariant.Accent}
          customButtonText="Logout"
          isCustomButtonVisible
        />
      </Loader.Overlay>
    )
  }

  if (!storage.getVerifiedWithTwoFa() && loginTwoFa) {
    return (
      <>
        <Loader.Overlay>
          <GoogleAuthenticatorForm
            onCancel={logoutWrapper}
            onSubmit={handleVerifyTwoFa}
            isReset
            errors={errors}
            setErrors={setErrors}
            isSubmitDisabled={verifiedWithTwoFaStatus === 'loading'}
          />
        </Loader.Overlay>
      </>
    )
  }

  if (profileStatus === 'failed')
    return (
      <Loader.Overlay>
        <ErrorBase {...errorBaseProps} />
      </Loader.Overlay>
    )

  if (isLoading || !isSetup || (isAuthenticated && profileStatus !== 'succeeded')) {
    return (
      <Loader.Overlay>
        <Loader.Content />
      </Loader.Overlay>
    )
  }

  if (error) {
    return (
      <Loader.Overlay>
        <ErrorBase
          customHeader={t({ message: `Authentication error`, id: 'core.authError' })}
          customText={JSON.stringify(error)}
          isRefreshButtonVisible
          isSupportMessage
        />
      </Loader.Overlay>
    )
  }

  if (isPublic && !isAuthenticated) {
    return <>{props.children}</>
  }

  return (
    <SetupProvider>
      <ExchangeProvider>
        <DataProvider>
          <UrlParamsProvider>{props.children}</UrlParamsProvider>
        </DataProvider>
      </ExchangeProvider>
    </SetupProvider>
  )
}
